import { z } from 'zod';
import { Resource, Shortlist, Tag } from '../../../__types__';
import { CoreFieldFilters } from '../../core/core-fields/core-fields-filters.types';
import { withCustomFieldFiltersSchema } from '../../core/custom-fields/custom-fields-filters.schemas';
import { WithCustomFieldFilters } from '../../core/custom-fields/custom-fields-filters.types';
import {
  PaginatedResponse,
  paginationSchema,
  paginationUtils
} from '../_common/query/pagination';
import {
  withBuilderUtils,
  withFiltersUtils,
  WithOptionalFilters,
  WithPagination,
  withPaginationUtils,
  WithSearch,
  withSearchUtils,
  WithSorter,
  withSorterUtils
} from '../_common/query/queryUtils';
import {
  BooleanFilter,
  booleanFilterSchema
} from '../_common/query/filters/BooleanFilter';
import { TimestampFilter } from 'v5/core/standard-date-filter/timestamp-filter';
import { enumFilterSchema } from '../_common/query/filters/EnumFilter';
import { ResourceSearchQuery } from '../resource/resource.api';
import {
  WithWorkspaceScoping,
  withWorkspaceScoping,
  workspaceScopingQueryUtils
} from '../_common/query/workspace-scoping';
import {
  WithTeamScoping,
  withTeamScoping,
  teamScopingQueryUtils
} from '../_common/query/team-scoping';

export const SHORTLIST_SORT_FIELDS = [
  'title',
  'createdAt',
  'updatedAt'
] as const;

export type ShortlistSortField = (typeof SHORTLIST_SORT_FIELDS)[number];

export type ShortlistTableColumnField =
  | ShortlistSortField
  | 'shortlisted'
  | 'tags'
  | 'status'
  | 'user_id';

export type ShortlistSearchQuery = WithSearch &
  WithPagination &
  WithSorter<ShortlistSortField> &
  WithOptionalFilters<
    WithCustomFieldFilters<CoreFieldFilters> & {
      published: BooleanFilter;
      publishedAt: TimestampFilter;
    }
  >;

/**
 * Browsing-scope lens (Layer 2). Enterprise shortlists are team+workspace;
 * org/legacy URLs leave both null.
 */
export type ShortlistScopedQuery = WithTeamScoping<
  WithWorkspaceScoping<ShortlistSearchQuery>
>;

export type ShortlistQuery = ShortlistSearchQuery | ShortlistScopedQuery;

export function asGlobalShortlistQuery<T extends ShortlistSearchQuery>(
  searchQuery: T
): ShortlistScopedQuery {
  return withTeamScoping(
    withWorkspaceScoping(searchQuery, workspaceScopingQueryUtils.ofGlobal()),
    teamScopingQueryUtils.ofGlobal()
  );
}

export type ShortlistListResponse = PaginatedResponse<Shortlist>;

export interface BulkArchiveRequest {
  ids: Shortlist['id'][];
}

export type ShortlistResourcesSearchQuery = ResourceSearchQuery & {
  shortlistId: Shortlist['id'];
};

/** Workspace and team are the browsing scope's; both null on org and legacy URLs. */
export interface CreateShortlistRequest {
  title: string;
  description: string | null;
  tags: Tag[];
  enterpriseWorkspaceId: number | null;
  enterpriseTeamId: number | null;
}

export interface ShortlistApi {
  getShortlists: (query: ShortlistQuery) => Promise<ShortlistListResponse>;
  getShortlist: (id: Shortlist['id']) => Promise<Shortlist>;
  createShortlist: (request: CreateShortlistRequest) => Promise<Shortlist>;
  bulkArchive: (request: BulkArchiveRequest) => Promise<void>;
  bulkUnarchive: (request: BulkArchiveRequest) => Promise<void>;

  searchShortlistResources: (
    query: ShortlistResourcesSearchQuery
  ) => Promise<PaginatedResponse<Resource['id']>>;
}

export const shortlistQueryUtils = withBuilderUtils({
  ...withSearchUtils<ShortlistSearchQuery>(),
  ...withFiltersUtils<ShortlistSearchQuery>(),
  ...withPaginationUtils<ShortlistSearchQuery>(),
  ...withSorterUtils<ShortlistSearchQuery>(),
  ofEmptySearchQuery: (): ShortlistSearchQuery => ({
    search: '',
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {}
  }),
  ofDefault: (): ShortlistSearchQuery => ({
    search: '',
    sort: { field: 'updatedAt', order: 'desc' },
    page: paginationUtils.ofDefault(),
    filters: {
      archived: { operator: 'equal', value: false }
    }
  })
}).setInitialQueryFactory(x => x.ofEmptySearchQuery);

const strToNull = (str: unknown) => (str === '' ? null : str);

export const shortlistSearchQuerySchema = z
  .object({
    search: z.preprocess(value => value || '', z.string()).default(''),
    filters: withCustomFieldFiltersSchema(
      z.object({
        published: z
          .preprocess(strToNull, booleanFilterSchema.nullable())
          .optional(),
        tags: z
          .preprocess(strToNull, enumFilterSchema(z.string()).nullable())
          .optional()
      })
    ).partial(),
    page: paginationSchema,
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(SHORTLIST_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .default(null)
  })
  .catch(error => {
    console.error('Error parsing shortlist search query', error.issues);
    return shortlistQueryUtils.ofEmptySearchQuery();
  });

export const shortlistQuerySchema = shortlistSearchQuerySchema;
